import { forwardRef, type FocusEvent, type InputHTMLAttributes } from 'react';

import { Field, type FieldWidth } from '../Field/Field';
import styles from './Input.module.css';

/**
 * Input — a single-line text field with the handoff's label / error contract.
 *
 * Three rules from the handoff's Forms section are encoded rather than
 * documented:
 *
 * 1. **Validate on blur, never on keystroke.** `onValidate` fires from
 *    `onBlur` and from nowhere else. There is no debounce, no "validate after
 *    the first error", no `onChange` path — a field that turns red while the
 *    agent is still typing the second digit of a mobile number is the failure
 *    this rule exists to prevent.
 * 2. **Required is marked; optional is never marked.** There is no `optional`
 *    prop to pass.
 * 3. **Width signals content length.** `width` has no default; see Field.
 *
 * The error message is announced by `role="alert"` on the message element
 * (Field), not by `aria-describedby` alone. That is not belt and braces: by the
 * time a blur-validated message exists, focus has left the field, and a
 * description is only read when its element receives focus.
 */

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'required' | 'className' | 'id'> {
  /** Visible label, rendered above the field. */
  label: string;
  /** Handoff, Forms 2. No default: a full-width input for a short value is a bug. */
  width: FieldWidth;
  /** Message describing the FIX. Present means invalid. */
  error?: string;
  /** Standing help text under the field. */
  hint?: string;
  required?: boolean;
  /** Called on blur with the field's current value. Never called while typing. */
  onValidate?: (value: string) => void;
  /**
   * Content that must not mirror: mobile numbers, case numbers, code
   * identifiers. Pins `dir="ltr"` and takes tabular figures.
   */
  numeric?: boolean;
  id?: string;
  /** Applied to the field wrapper, not to the input. */
  className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    width,
    error,
    hint,
    required = false,
    onValidate,
    numeric = false,
    id,
    className,
    disabled = false,
    onBlur,
    type = 'text',
    dir,
    ...rest
  },
  ref,
) {
  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    // The whole of "validate on blur, never on keystroke" is these two lines
    // plus the absence of any onChange counterpart.
    onValidate?.(event.target.value);
    onBlur?.(event);
  }

  return (
    <Field
      label={label}
      width={width}
      required={required}
      error={error}
      hint={hint}
      disabled={disabled}
      id={id}
      className={className}
    >
      {({ widthClassName, className: controlClassName, ...control }) => (
        <input
          {...rest}
          {...control}
          ref={ref}
          type={type}
          // Handoff, "Never mirrors — wrap each in dir='ltr'". An explicit `dir`
          // from the caller still wins; `numeric` is the default for the case
          // the handoff names.
          dir={dir ?? (numeric ? 'ltr' : undefined)}
          inputMode={rest.inputMode ?? (numeric ? 'numeric' : undefined)}
          className={[
            controlClassName,
            widthClassName,
            styles['input'],
            numeric ? styles['numeric'] : null,
          ]
            .filter(Boolean)
            .join(' ')}
          onBlur={handleBlur}
        />
      )}
    </Field>
  );
});
